import { Typography } from '@mui/material';
import { filter, first, forEach, includes, isEmpty, join, keys, size, uniq, values, xor } from 'lodash';
import React from 'react';

import { InferenceModelData, ModelInference, OrchestrationInference } from 'interfaces/calculateFeatures';
import { Slide } from 'interfaces/slide';
import { getModelId, getOverrideAnnotationsTimestamp } from 'utils/models';
import {
  ConfirmationModalMessageText,
  getClassConfigErrorValidation,
  getTSMModelErrorValidation,
  getTSMModelErrorWithOverrideAnnotationsValidation,
} from './ConfirmationModalMessageText';

export const validateNewOrchestration = ({
  newOrchestration,
  slideIds,
  slides,
  selectedOrchestrations,
  selectedModelType,
  modelInference,
  modelType,
  stain,
  modelId,
  setSelectedOrchestrations,
  setRowModelSelectedOrchestrations,
}: {
  newOrchestration: OrchestrationInference;
  slideIds: string[];
  slides: Record<string, Pick<Slide, 'stainingType' | 'originalFileName'>>;
  selectedOrchestrations: any;
  selectedModelType: string;
  modelInference: ModelInference;
  modelType: string;
  stain: string;
  modelId: string;
  setSelectedOrchestrations: (
    slideIds: string[],
    model: Partial<InferenceModelData>,
    modelType: string,
    orchestration: OrchestrationInference
  ) => void;
  setRowModelSelectedOrchestrations: (slideIds: string[], orchestration: OrchestrationInference) => void;
}) => {
  let message: { title?: string; text?: React.ReactNode; onConfirm?: () => void } = {};
  let currentTsmModelsForSpecificModel: string[] = [];
  let currentClassConfig: string[] = [];
  let currentTsmModelUrlPerSlide: Record<string, string> = {};
  let currentTsmModelPerSlide: Record<
    string,
    {
      modelUrl: string;
      overrideAnnotationsTimestamp: string;
    }
  > = {};

  // validate tsm model only if the new orchestration has a tsm model
  if (!isEmpty(newOrchestration?.params?.deps?.modelTsm)) {
    // validate tsm model per slide
    forEach(slideIds, (slideId) => {
      if (
        !isEmpty(selectedOrchestrations?.[slideId]) &&
        // check if the slide has only one model type selected and it's the same model type as the new orchestration
        !(size(selectedOrchestrations[slideId]) === 1 && !isEmpty(selectedOrchestrations[slideId][selectedModelType]))
      ) {
        // get the first selected orchestration that have a tsm model
        const curOrchestration = first(
          filter(
            values(selectedOrchestrations[slideId]),
            (selectedOrchestration) => !isEmpty(selectedOrchestration?.orchestration?.params?.deps?.modelTsm)
          )
        );

        const curModelTsmUrlPattern =
          curOrchestration?.orchestration?.tsmSlideOverrides?.[slideId] ??
          curOrchestration?.orchestration?.orchestrationTsmArtifactUrlPattern;
        const curModelTsmUrl = curOrchestration?.orchestration?.params?.deps?.modelTsm;
        const curModelTsmId = getModelId(curModelTsmUrl);

        const newOrchestrationTsmUrlPattern =
          newOrchestration?.tsmSlideOverrides?.[slideId] ?? newOrchestration?.orchestrationTsmArtifactUrlPattern;

        if (!isEmpty(curModelTsmId) && curModelTsmId !== getModelId(newOrchestration?.params?.deps?.modelTsm)) {
          currentTsmModelUrlPerSlide[slideId] = curModelTsmUrl;
        }
        // else, check if the tsm url pattern is different- mean that the tsm url is the same but the override_annotations_timestamp is different
        else if (!isEmpty(curModelTsmUrlPattern) && curModelTsmUrlPattern !== newOrchestrationTsmUrlPattern) {
          currentTsmModelPerSlide[slideId] = {
            modelUrl: curModelTsmUrl,
            overrideAnnotationsTimestamp: getOverrideAnnotationsTimestamp(curModelTsmUrlPattern),
          };
        }
      }
    });
  }

  forEach(selectedOrchestrations, (orchestrationsByModel, slideId) => {
    if (
      slides?.[slideId]?.stainingType === stain &&
      !isEmpty(orchestrationsByModel?.[selectedModelType]) &&
      !includes(slideIds, slideId)
    ) {
      // validate class config- Check if there are any classes that don't match each other between the new orchestration and the previous selected orchestrations
      if (
        orchestrationsByModel[selectedModelType].model?.modelUrl !== modelInference?.modelUrl &&
        !isEmpty(xor(orchestrationsByModel[selectedModelType].model?.classes, modelInference?.classes))
      ) {
        currentClassConfig = orchestrationsByModel[selectedModelType]?.model?.classes;
        return false;
      }

      // validate tsm model per model type
      if (
        orchestrationsByModel[selectedModelType]?.orchestration.orchestrationId !== newOrchestration?.orchestrationId
      ) {
        currentTsmModelsForSpecificModel.push(
          orchestrationsByModel[selectedModelType]?.orchestration?.params?.deps?.modelTsm
        );
      }
    }
  });

  // class config validation and tsm model per slide validation
  if (!isEmpty(currentClassConfig) && (!isEmpty(currentTsmModelUrlPerSlide) || !isEmpty(currentTsmModelPerSlide))) {
    message.title = `Class Config and Tissue Segmentation Mask Model Error${
      isEmpty(currentTsmModelPerSlide) && ' With Override Annotations'
    }`;
    message.onConfirm = () => {
      // unselect the previous selected orchestrations
      const previousSelectedOrchestrationsSlideIds: string[] = [];
      forEach(selectedOrchestrations, (orchestrationsByModel, slideId) => {
        if (slides[slideId].stainingType === stain && !isEmpty(orchestrationsByModel[selectedModelType])) {
          previousSelectedOrchestrationsSlideIds.push(slideId);
        }
      });
      setSelectedOrchestrations(previousSelectedOrchestrationsSlideIds, { modelType }, selectedModelType, null);
      setSelectedOrchestrations(
        keys(currentTsmModelUrlPerSlide ?? currentTsmModelPerSlide),
        null,
        selectedModelType,
        null
      );
      setRowModelSelectedOrchestrations(slideIds, newOrchestration);
    };

    message.text = (
      <ConfirmationModalMessageText
        modelType={selectedModelType}
        modelId={modelId}
        stain={stain}
        slideIds={keys(currentTsmModelUrlPerSlide ?? currentTsmModelPerSlide)}
        messageType="Error"
        validations={[
          getClassConfigErrorValidation(currentClassConfig, modelInference?.classes),
          currentTsmModelUrlPerSlide
            ? getTSMModelErrorValidation(currentTsmModelUrlPerSlide, newOrchestration)
            : getTSMModelErrorWithOverrideAnnotationsValidation(currentTsmModelPerSlide, newOrchestration),
        ]}
      />
    );
  } else if (!isEmpty(currentTsmModelUrlPerSlide)) {
    message.title = 'TSM Model Error';
    message.onConfirm = () => {
      setSelectedOrchestrations(keys(currentTsmModelUrlPerSlide), null, selectedModelType, null);
      setRowModelSelectedOrchestrations(slideIds, newOrchestration);
    };
    message.text = (
      <ConfirmationModalMessageText
        modelType={selectedModelType}
        modelId={modelId}
        stain={stain}
        slideIds={keys(currentTsmModelUrlPerSlide)}
        messageType="Error"
        validations={[getTSMModelErrorValidation(currentTsmModelUrlPerSlide, newOrchestration)]}
      />
    );
  } else if (!isEmpty(currentTsmModelPerSlide)) {
    message.title = 'TSM Model Error With Override Annotations';
    message.onConfirm = () => {
      setSelectedOrchestrations(keys(currentTsmModelPerSlide), null, selectedModelType, null);
      setRowModelSelectedOrchestrations(slideIds, newOrchestration);
    };
    message.text = (
      <ConfirmationModalMessageText
        modelType={selectedModelType}
        modelId={modelId}
        stain={stain}
        slideIds={keys(currentTsmModelPerSlide)}
        messageType="Error"
        validations={[getTSMModelErrorWithOverrideAnnotationsValidation(currentTsmModelPerSlide, newOrchestration)]}
      />
    );
  } else if (!isEmpty(currentClassConfig)) {
    message.title = 'Class Config Error';
    message.onConfirm = () => {
      // unselect the previous selected orchestrations
      const previousSelectedOrchestrationsSlideIds: string[] = [];
      forEach(selectedOrchestrations, (orchestrationsByModel, slideId) => {
        if (slides?.[slideId]?.stainingType === stain && !isEmpty(orchestrationsByModel?.[selectedModelType])) {
          previousSelectedOrchestrationsSlideIds.push(slideId);
        }
      });
      setSelectedOrchestrations(previousSelectedOrchestrationsSlideIds, { modelType }, selectedModelType, null);
      setRowModelSelectedOrchestrations(slideIds, newOrchestration);
    };
    message.text = (
      <ConfirmationModalMessageText
        modelType={selectedModelType}
        modelId={modelId}
        stain={stain}
        slideIds={slideIds}
        messageType="Error"
        validations={[getClassConfigErrorValidation(currentClassConfig, modelInference?.classes)]}
      />
    );
  } else if (
    !isEmpty(currentTsmModelsForSpecificModel) &&
    !includes(currentTsmModelsForSpecificModel, newOrchestration.params.deps.modelTsm)
  ) {
    // check if the new orchestration have the same tsm model as the other selected orchestrations for the same stain slides
    message.title = 'TSM Model Warning';
    message.onConfirm = () => setRowModelSelectedOrchestrations(slideIds, newOrchestration);
    message.text = (
      <ConfirmationModalMessageText
        modelType={selectedModelType}
        modelId={modelId}
        stain={stain}
        slideIds={slideIds}
        messageType="Warning"
        validations={[
          {
            type: 'TsmModel',
            currentTypeData: (
              <Typography variant="h4"> {join(uniq(currentTsmModelsForSpecificModel), ', ')}</Typography>
            ),
            newTypeData: newOrchestration.params.deps.modelTsm,
          },
        ]}
      />
    );
  }

  return message;
};
